import {
  CLICK_DEFAULT_FREQUENCY,
  COLLECT_REPLY_LIMITS,
  CONNECTOR_LIMITS,
  FACING_ONLY_TRIGGERS,
  isFacingType,
  PAGE_SECONDS_DEFAULT,
  UNRESPONSIVE_MINUTES_DEFAULT,
  WORKFLOW_FIRST_PATH,
  WORKFLOW_HOOK,
  WORKFLOW_PROBLEM_SEPARATOR,
  type WorkflowKind,
  type WorkflowStepType,
  type WorkflowTrigger,
} from "@sagot/constants";
import { isSystemStep, newSystemStep } from "./builder/system/system-model";
import { isServiceStep, isTicketFacingStep, newServiceStep } from "./service/service-model";
import { exitSlotsOf, withExit, type ExitKey } from "./exit-slots";
import { releaseNotesOf } from "./notes-model";
import { pruneTranslations } from "./translations-model";
import type { ActionStep, Conditions, Definition, FacingStep, Notes, PathDef, ReplyButton, Step, Translations, WorkflowSettings } from "./types";

export { exitSlotsOf, withExit, type ExitFallback, type ExitKey, type ExitRef, type ExitSlot } from "./exit-slots";

const ALPHABET = Array.from({ length: 26 }, (_, index) => String.fromCharCode(65 + index));
const STEP_ID_MAX = 40;
/** Picked in a path list to make the step create its own new path (resolved by `materializeNewPaths`, never stored). */
export const NEW_PATH = "+new";

export const emptyConditions = (): Conditions => ({ any: [] });

export function emptyDefinition(): Definition {
  return { settings: { agent_ids: [], channels: [], schedule: "any", conditions: emptyConditions() }, paths: { [WORKFLOW_FIRST_PATH]: { steps: [] } } };
}

interface StoredDraft {
  settings?: Partial<Omit<WorkflowSettings, "conditions">> & { conditions?: Partial<Conditions> };
  paths?: Record<string, Partial<PathDef>>;
  translations?: Translations;
  notes?: Notes;
}

/** The stored draft as an editable definition (missing pieces filled with defaults). */
export function definitionFrom(raw: unknown): Definition {
  const base = emptyDefinition();
  if (typeof raw !== "object" || raw === null) return base;
  const draft = raw as StoredDraft;
  const stored = draft.settings ?? {};
  const settings: WorkflowSettings = {
    ...stored,
    agent_ids: stored.agent_ids ?? [],
    channels: stored.channels ?? [],
    schedule: stored.schedule ?? "any",
    conditions: { any: stored.conditions?.any ?? [] },
  };
  const translations = draft.translations && Object.keys(draft.translations).length > 0 ? { translations: draft.translations } : {};
  const notes = draft.notes && Object.keys(draft.notes).length > 0 ? { notes: draft.notes } : {};
  const entries = Object.entries(draft.paths ?? {});
  if (entries.length === 0) return { ...base, settings, ...translations, ...notes };
  return { settings, paths: Object.fromEntries(entries.map(([id, path]) => [id, { ...path, steps: path.steps ?? [] }])), ...translations, ...notes };
}

/** Every step id already used (including the actions inside Apply rules). */
export function stepIds(definition: Definition): Set<string> {
  const ids = new Set<string>();
  for (const path of Object.values(definition.paths)) {
    for (const step of path.steps) {
      ids.add(step.id);
      if (step.type === "apply_rules") for (const rule of step.rules) for (const action of rule.actions) ids.add(action.id);
    }
  }
  return ids;
}

/** `send_webhook` → `send_webhook`, then `send_webhook_2` … (backend `STEP_ID_PATTERN`). */
export function freshStepId(type: string, taken: ReadonlySet<string>): string {
  const base = type.slice(0, STEP_ID_MAX - 3);
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}_${String(n)}`;
  return id;
}

/** The next free button id (`b1`, `b2` …) of one Reply buttons step. */
export function freshButtonId(buttons: readonly ReplyButton[]): string {
  const taken = new Set(buttons.map((button) => button.id));
  let n = 1;
  while (taken.has(`b${String(n)}`)) n += 1;
  return `b${String(n)}`;
}

/** The next free path letter (`A`, `B` … `Z`), or `null` when all are used. */
export function nextPathId(definition: Definition): string | null {
  return ALPHABET.find((letter) => !(letter in definition.paths)) ?? null;
}

/** A new step with sensible defaults; ids come from `freshStepId`. */
export function newStep(type: WorkflowStepType, id: string, firstPath = WORKFLOW_FIRST_PATH): Step {
  switch (type) {
    case "send_webhook":
      return { id, type, endpoint_id: "", body: "standard" };
    case "add_tags":
    case "remove_tags":
      return { id, type, tags: [] };
    case "assign":
      return { id, type, to: "team" };
    case "set_status":
      return { id, type, status: "resolved" };
    case "add_note":
      return { id, type, text: "" };
    case "update_lead":
      return { id, type, sync_crm: false };
    case "notify_staff":
      return { id, type, to: "owners_admins", title: "", email: true };
    case "apply_sla":
      return { id, type, policy_id: "" };
    case "set_priority":
      return { id, type, priority: "high" };
    case "email_visitor":
      return { id, type, subject: "", body: "" };
    case "set_attribute":
      return { id, type, object: "visitor", key: "", value: "" };
    case "wait":
      return { id, type, duration: { value: UNRESPONSIVE_MINUTES_DEFAULT, unit: "minutes" }, interrupt_on: ["visitor_reply"] };
    case "wait_for_webhook":
      return { id, type, timeout_hours: WORKFLOW_HOOK.timeoutHoursDefault };
    case "run_workflow":
      return { id, type, workflow_id: "" };
    case "snooze":
      return { id, type, for: { value: 1, unit: "hours" } };
    case "set_language":
      return { id, type, language: "fil" };
    case "add_summary_note":
      return { id, type, notify_assignee: false };
    case "show_article":
      return { id, type, source_id: "" };
    case "http_request":
      return { id, type, endpoint_id: "", method: "GET", timeout_ms: CONNECTOR_LIMITS.timeoutMsDefault, map: [] };
    case "ask_rating":
      return { id, type, text: "", scale: "stars", ask_comment: true };
    case "branch":
      return { id, type, branches: [{ label: "", when: emptyConditions(), goto: firstPath }] };
    case "apply_rules":
      return { id, type, rules: [{ label: "", when: emptyConditions(), actions: [] }] };
    case "go_to_path":
      return { id, type, path: firstPath };
    case "end":
      return { id, type };
    case "send_message":
      return { id, type, text: "" };
    case "reply_buttons":
      return { id, type, text: "", allow_typing: true, buttons: [{ id: "b1", label: "", goto: "" }, { id: "b2", label: "", goto: "" }] };
    case "collect_data":
      return { id, type, text: "", field: { type: "text", label: "", required: true }, save_to: `var.${id}`, skip_if_known: true };
    case "collect_reply":
      return { id, type, text: "", save_to: `var.${id}`, format: "any", min_length: COLLECT_REPLY_LIMITS.minDefault, max_length: COLLECT_REPLY_LIMITS.maxDefault };
    case "verify_email":
      return { id, type, text: "" };
    case "disable_reply":
    case "enable_reply":
      return { id, type };
    case "let_assistant_answer":
    case "show_booking":
    case "show_lead_form":
    case "expected_reply_time":
      return { id, type };
    case "hand_to_person":
      return { id, type, to: "default" };
    case "notify_team":
    case "route_handoff":
    case "assign_lead_owner":
    case "send_to_subscribed_endpoints":
      return newSystemStep(type, id);
    case "create_ticket":
    case "update_ticket":
    case "set_ticket_state":
    case "route_ticket":
    case "assign_ticket":
    case "add_ticket_note":
    case "reply_to_ticket":
    case "notify_contact":
    case "link_ticket":
    case "apply_ticket_sla":
    case "request_csat":
    case "ticket_intake":
    case "show_ticket_form":
    case "show_my_tickets":
    case "lookup_asset":
    case "schedule_visit":
    case "lookup_catalogue":
    case "check_order_status":
    case "show_catalogue":
    case "create_order":
    case "show_order_status":
      return newServiceStep(type, id);
  }
}

/** A step the visitor sees whose form is one of the content steps (the three ticket steps the visitor sees have their own: `isTicketFacingStep`). */
export function isFacingStep(step: Step): step is FacingStep {
  return isFacingType(step.type) && !isTicketFacingStep(step);
}

/** Whether the visitor sees or answers the step: a content step, or one of the ticket steps (intake, form, my tickets). */
export const talksToVisitor = (step: Step): boolean => isFacingType(step.type);

export function isAction(step: Step): step is ActionStep {
  return !["branch", "apply_rules", "wait", "wait_for_webhook", "http_request", "snooze", "go_to_path", "end"].includes(step.type) && !isFacingStep(step) && !isSystemStep(step) && !isServiceStep(step);
}

/** A workflow is customer-facing as soon as it has a step the visitor sees. */
export function kindOf(definition: Definition): WorkflowKind {
  const facing = Object.values(definition.paths).some((path) => path.steps.some(talksToVisitor));
  return facing ? "facing" : "background";
}

/** The kind the API reports; for older responses it is computed from the draft or, failing that, from the trigger. */
export function kindOfWorkflow(workflow: { kind?: string | null; trigger: string; draft?: unknown }): WorkflowKind {
  if (workflow.kind === "facing" || workflow.kind === "background") return workflow.kind;
  if (workflow.draft !== undefined) return kindOf(definitionFrom(workflow.draft));
  return FACING_ONLY_TRIGGERS.has(workflow.trigger) ? "facing" : "background";
}

/** The edited path replaced; translations of steps and buttons the edit removed go with them (the server refuses dangling ones). */
function withPath(definition: Definition, pathId: string, change: (path: PathDef) => PathDef): Definition {
  const path = definition.paths[pathId];
  if (!path) return definition;
  return pruneTranslations({ ...definition, paths: { ...definition.paths, [pathId]: change(path) } });
}

export function addStep(definition: Definition, pathId: string, step: Step): Definition {
  return withPath(definition, pathId, (path) => ({ ...path, steps: [...path.steps, step] }));
}

export function replaceStep(definition: Definition, pathId: string, index: number, step: Step): Definition {
  return withPath(definition, pathId, (path) => ({ ...path, steps: path.steps.map((s, i) => (i === index ? step : s)) }));
}

export function removeStep(definition: Definition, pathId: string, index: number): Definition {
  return withPath(definition, pathId, (path) => ({ ...path, steps: path.steps.filter((_, i) => i !== index) }));
}

export function moveStep(definition: Definition, pathId: string, from: number, to: number): Definition {
  return withPath(definition, pathId, (path) => {
    if (to < 0 || to >= path.steps.length) return path;
    const steps = [...path.steps];
    const [moved] = steps.splice(from, 1);
    if (moved) steps.splice(to, 0, moved);
    return { ...path, steps };
  });
}

export function addPath(definition: Definition): { definition: Definition; pathId: string | null } {
  const pathId = nextPathId(definition);
  if (!pathId) return { definition, pathId: null };
  return { definition: { ...definition, paths: { ...definition.paths, [pathId]: { steps: [] } } }, pathId };
}

/**
 * Rewrites every path a step leads to, exit by exit (see `exit-slots.ts`). A required exit (branch, Go to path, reply
 * button) keeps `""` for "not chosen"; an optional exit is dropped when the new value is empty. Exits not connected are left alone.
 */
export function mapGotos(step: Step, change: (goto: string) => string): Step {
  return exitSlotsOf(step).reduce((current, slot) => (slot.target ? withExit(current, slot.key, change(slot.target)) : current), step);
}

/** Every path a step can lead to (empty exits left out). */
export function targetsOf(step: Step): string[] {
  return exitSlotsOf(step).flatMap((slot) => (slot.target ? [slot.target] : []));
}

/** The exit `key` of the step at `at` pointed at `target` (`undefined` disconnects it); translations of what the edit removed are pruned like any step edit. */
export function setExit(definition: Definition, at: { path: string; index: number }, key: ExitKey, target: string | undefined): Definition {
  const step = definition.paths[at.path]?.steps[at.index];
  return step ? replaceStep(definition, at.path, at.index, withExit(step, key, target)) : definition;
}

/** Points an exit at a path created for it (the next free letter); `null` while all 26 paths are used. */
export function connectToNewPath(definition: Definition, at: { path: string; index: number }, key: ExitKey): { definition: Definition; pathId: string } | null {
  const added = addPath(definition);
  if (!added.pathId) return null;
  return { definition: setExit(added.definition, at, key, added.pathId), pathId: added.pathId };
}

/** Gives every "New path" pick of an edited step its own empty path (`B`, `C` ...). */
export function materializeNewPaths(definition: Definition, step: Step): { definition: Definition; step: Step } {
  let current = definition;
  const resolved = mapGotos(step, (goto) => {
    if (goto !== NEW_PATH) return goto;
    const added = addPath(current);
    current = added.definition;
    return added.pathId ?? "";
  });
  return { definition: current, step: resolved };
}

/** Removes a path and every reference to it (Path A stays). Chosen exits that led there become "not chosen"; its notes become notes of the workflow. */
export function removePath(definition: Definition, pathId: string): Definition {
  if (pathId === WORKFLOW_FIRST_PATH) return definition;
  const paths = Object.fromEntries(Object.entries(definition.paths).filter(([id]) => id !== pathId));
  const unlink = (step: Step): Step | null => {
    if (step.type === "go_to_path") return step.path === pathId ? null : step;
    if (step.type === "branch") {
      const branches = step.branches.filter((branch) => branch.goto !== pathId);
      return { ...step, branches, else_goto: step.else_goto === pathId ? undefined : step.else_goto };
    }
    return mapGotos(step, (goto) => (goto === pathId ? "" : goto));
  };
  const cleaned = Object.fromEntries(
    Object.entries(paths).map(([id, path]) => [id, { ...path, steps: path.steps.map(unlink).filter((s): s is Step => s !== null) }]),
  );
  return pruneTranslations(releaseNotesOf({ ...definition, paths: cleaned }, pathId));
}

/** The `settings.trigger_options` a new workflow of this trigger starts with (none for triggers without options). */
export function defaultTriggerOptions(trigger: WorkflowTrigger): WorkflowSettings["trigger_options"] {
  if (trigger === "visitor_unresponsive" || trigger === "staff_unresponsive") return { minutes: UNRESPONSIVE_MINUTES_DEFAULT };
  if (trigger === "visitor_visits_page") return { url_rules: [], seconds_on_page: PAGE_SECONDS_DEFAULT, frequency: "once" };
  if (trigger === "visitor_clicks_element") return { url_rules: [], frequency: CLICK_DEFAULT_FREQUENCY };
  return undefined;
}

/** Problems of a refused save (`error.problems`, one per line). */
export function problemsOf(details: unknown): string[] {
  if (typeof details !== "string" || !details) return [];
  return details.split(WORKFLOW_PROBLEM_SEPARATOR).filter(Boolean);
}

/** Paths a step can lead to (for the picker), Path A excluded where a loop back would be refused. */
export function pathChoices(definition: Definition, current: string): string[] {
  return Object.keys(definition.paths).filter((id) => id !== current).sort();
}

const STEP_PROBLEM = /^step ([a-z0-9_.]+):/;
const PATH_PROBLEM = /^paths\.([A-Z]{1,2})\.steps\.(\d+)/;

/** Step ids a list of save problems points at (flagged on their cards). */
export function problemStepIds(problems: readonly string[], definition: Definition): Set<string> {
  const ids = new Set<string>();
  for (const problem of problems) {
    const byId = STEP_PROBLEM.exec(problem);
    if (byId?.[1]) ids.add(byId[1].split(".")[0] ?? byId[1]);
    const byPath = PATH_PROBLEM.exec(problem);
    if (byPath?.[1] && byPath[2]) {
      const step = definition.paths[byPath[1]]?.steps[Number(byPath[2])];
      if (step) ids.add(step.id);
    }
  }
  return ids;
}
